import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Col, Empty, Form, Input, InputNumber, List, Modal, Row, Space, Tag, Tooltip, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, AppstoreOutlined, LockOutlined } from '@ant-design/icons';
import { get, post, put, del } from '../../../api/client';
import type { FeatureRow } from '../types';

const TEAL = '#0d9488';

/**
 * 功能权限项（catalog）管理：定义有哪些可控功能项（key/name/分组）。
 * 具体某等级是否开放某功能，在下方「等级功能权限矩阵」中配置。
 * - GET/POST/PUT/DELETE /admin/membership/features
 * - is_system 的系统内置项不可编辑/删除（后端拦截，前端禁用按钮）。
 */
export function FeaturesManager() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<FeatureRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const { data, isLoading } = useQuery({
    queryKey: ['membership-features'],
    queryFn: () => get<FeatureRow[]>('/admin/membership/features'),
  });
  const features = data ?? [];

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['membership-features'] });
    void qc.invalidateQueries({ queryKey: ['membership-compare'] });
  };

  const save = useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Record<string, unknown> }) =>
      id ? put(`/admin/membership/features/${id}`, values) : post('/admin/membership/features', values),
    onSuccess: () => {
      message.success('功能项已保存');
      setEditing(null);
      setCreating(false);
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '保存失败'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => del(`/admin/membership/features/${id}`),
    onSuccess: () => {
      message.success('功能项已删除');
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '删除失败'),
  });

  const openCreate = () => {
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ sortOrder: features.length });
  };

  const openEdit = (f: FeatureRow) => {
    setEditing(f);
    form.setFieldsValue({
      key: f.key,
      name: f.name,
      description: f.description ?? '',
      groupName: f.groupName ?? '',
      sortOrder: f.sortOrder,
    });
  };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      save.mutate({
        id: editing?.id,
        values: {
          ...(editing ? {} : { key: v.key }),
          name: v.name,
          description: v.description || undefined,
          groupName: v.groupName || undefined,
          sortOrder: v.sortOrder ?? 0,
        },
      });
    } catch {
      /* 校验失败 */
    }
  };

  const askDelete = (f: FeatureRow) => {
    modal.confirm({
      title: '删除功能项',
      content: `确认删除功能项「${f.name}」？各等级下该项的开关配置将一并移除。`,
      okButtonProps: { danger: true },
      okText: '删除',
      cancelText: '取消',
      onOk: () => remove.mutateAsync(f.id),
    });
  };

  return (
    <Card
      title={
        <Space>
          <AppstoreOutlined style={{ color: TEAL }} />
          <span>功能权限项</span>
          <Tag>{features.length}</Tag>
        </Space>
      }
      extra={
        <Button type="primary" ghost icon={<PlusOutlined />} onClick={openCreate}>
          新建功能项
        </Button>
      }
      style={{ marginBottom: 16 }}
    >
      {isLoading ? (
        <Card loading style={{ boxShadow: 'none' }} />
      ) : features.length === 0 ? (
        <Empty description="暂无功能权限项" />
      ) : (
        <List
          dataSource={features}
          split
          renderItem={(f) => (
            <List.Item
              style={{ paddingInline: 4 }}
              actions={[
                <Tooltip title={f.isSystem ? '系统内置项不可编辑' : '编辑'} key="edit">
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    disabled={f.isSystem}
                    onClick={() => openEdit(f)}
                  />
                </Tooltip>,
                <Tooltip title={f.isSystem ? '系统内置项不可删除' : '删除'} key="del">
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={f.isSystem}
                    onClick={() => askDelete(f)}
                  />
                </Tooltip>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Space size={8} wrap>
                    <Typography.Text strong>{f.name}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                      {f.key}
                    </Typography.Text>
                    {f.groupName && <Tag color="cyan">{f.groupName}</Tag>}
                    {f.isSystem && (
                      <Tag icon={<LockOutlined />} color="default">
                        系统内置
                      </Tag>
                    )}
                  </Space>
                }
                description={f.description || <span style={{ color: '#b6bcc2' }}>暂无描述</span>}
              />
            </List.Item>
          )}
        />
      )}

      <Modal
        title={editing ? '编辑功能项' : '新建功能项'}
        open={creating || editing !== null}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={submit}
        okText="保存"
        cancelText="取消"
        confirmLoading={save.isPending}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item
            name="key"
            label="功能标识（key）"
            tooltip="唯一英文标识，创建后不可修改，前端据此判定权限"
            rules={[
              { required: true, message: '请输入功能标识' },
              { pattern: /^[a-z0-9_:.-]+$/, message: '仅允许小写字母、数字与 _ : . -' },
            ]}
          >
            <Input placeholder="如 export.excel" disabled={!!editing} />
          </Form.Item>
          <Form.Item name="name" label="功能名称" rules={[{ required: true, message: '请输入功能名称' }]}>
            <Input placeholder="如 导出 Excel" />
          </Form.Item>
          <Form.Item name="groupName" label="所属分组" tooltip="用于矩阵中分组展示，如 数据能力 / 客服 / 财务">
            <Input placeholder="如 数据能力" />
          </Form.Item>
          <Form.Item name="description" label="功能描述">
            <Input.TextArea rows={2} placeholder="简要说明该功能" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="sortOrder" label="排序">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Card>
  );
}
